import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import MicIcon from "@mui/icons-material/Mic";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import { resolveSearchRoute, matchBackendQuery } from "../../../utils/searchRoutes";
import "./NavbarSearch.css";

const resolveComponent = (comp, fallback = "div") => {
  if (!comp) return fallback;
  if (comp.default && (typeof comp.default === "function" || typeof comp.default === "object")) {
    return comp.default;
  }
  return comp;
};

const SearchComp = resolveComponent(SearchIcon, () => null);
const MicComp = resolveComponent(MicIcon, () => null);
const CloseComp = resolveComponent(CloseIcon, () => null);
const ArrowBackComp = resolveComponent(ArrowBackIcon, () => null);
const RefreshComp = resolveComponent(RefreshIcon, () => null);

const getSpeechRecognitionClass = () => {
  if (typeof window === "undefined") return null;
  return (
    window.SpeechRecognition ||
    window.webkitSpeechRecognition ||
    window.mozSpeechRecognition ||
    window.msSpeechRecognition ||
    null
  );
};

const NavbarSearch = ({
  className = "",
  placeholder = "Search...",
  onSearch,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  // Voice Search Modal States
  const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState("idle"); // "idle" | "listening" | "speaking" | "searching" | "error"
  const [errorMessage, setErrorMessage] = useState("");
  const [matchedQuery, setMatchedQuery] = useState(null);
  const [noMatchFound, setNoMatchFound] = useState(false);

  const recognitionRef = useRef(null);
  const autoSearchTimerRef = useRef(null);
  const inputRef = useRef(null);
  const overlayInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const currentPath = location.pathname;
  // Sync search input with URL search parameter if present
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const urlQuery = params.get("search");
      if (urlQuery && urlQuery !== searchQuery) {
        setSearchQuery(urlQuery);
      }
    } catch (e) {}
  }, [location.search]);

  // Close overlay, voice modal & stop listening when route changes
  useEffect(() => {
    setIsOverlayOpen(false);
    setIsVoiceModalOpen(false);
    stopListening();
  }, [location.pathname]);

  // Freeze background scroll when overlay or voice modal is open
  useEffect(() => {
    if (isOverlayOpen || isVoiceModalOpen) {
      document.body.style.overflow = "hidden";
      if (isOverlayOpen && !isVoiceModalOpen) {
        const timer = setTimeout(() => {
          if (overlayInputRef.current) {
            overlayInputRef.current.focus();
          }
        }, 60);
        return () => {
          clearTimeout(timer);
          document.body.style.overflow = "";
        };
      }
      return () => {
        document.body.style.overflow = "";
      };
    } else {
      document.body.style.overflow = "";
    }
  }, [isOverlayOpen, isVoiceModalOpen]);

  // Dismiss modal or overlay with Escape key
  useEffect(() => {
    const handleKeyDownGlobal = (e) => {
      if (e.key === "Escape") {
        if (isVoiceModalOpen) {
          closeVoiceModal();
        } else if (isOverlayOpen) {
          setIsOverlayOpen(false);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDownGlobal);
    return () => window.removeEventListener("keydown", handleKeyDownGlobal);
  }, [isVoiceModalOpen, isOverlayOpen]);

  const triggerSearch = (query) => {
    const term = (query !== undefined ? query : searchQuery).trim();
    if (isOverlayOpen) {
      setIsOverlayOpen(false);
    }
    if (typeof onSearch === "function") {
      onSearch(term);
      return;
    }
    if (!term) return;

    const isMarketPage =
      currentPath.startsWith("/buy-") ||
      currentPath.startsWith("/find-") ||
      currentPath.startsWith("/Buy") ||
      currentPath.startsWith("/recycle") ||
      currentPath.startsWith("/Farming") ||
      currentPath.startsWith("/single-item") ||
      currentPath.startsWith("/rawmaterial-") ||
      currentPath.startsWith("/manufacturer-") ||
      currentPath.startsWith("/wholeseller-") ||
      currentPath.startsWith("/retailer-");

    // Match query against backend data / categories (extracts keyword from large text)
    const match = matchBackendQuery(term);
    const searchTerm = match && match.query ? match.query : term;

    // Dispatch global voice search event so active page can immediately filter data
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("selectezi:voicesearch", {
          detail: { query: searchTerm, rawQuery: term, pathname: currentPath },
        })
      );
    }

    if (isMarketPage) {
      // Keep user on their active market page and update search query
      navigate(`${currentPath}?search=${encodeURIComponent(searchTerm)}`);
    } else if (match && match.route) {
      // Route to matching marketplace with matched query
      navigate(`${match.route}?search=${encodeURIComponent(searchTerm)}`);
    } else {
      // Intelligently resolve spoken or typed term to the matching marketplace
      const target = resolveSearchRoute(term);
      navigate(`${target.route}?search=${encodeURIComponent(searchTerm)}`);
    }
  };

  const startVoiceSearch = () => {
    const SpeechRecognitionClass = getSpeechRecognitionClass();
    if (!SpeechRecognitionClass) {
      setErrorMessage("Voice search is not supported in this browser. Please use Chrome or Edge.");
      setVoiceStatus("error");
      return;
    }

    try {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }

      const recognition = new SpeechRecognitionClass();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      recognition.onstart = () => {
        setIsListening(true);
        setVoiceStatus("listening");
        setErrorMessage("");
      };

      recognition.onspeechstart = () => {
        setIsSpeaking(true);
        setVoiceStatus("speaking");
      };

      recognition.onsoundstart = () => {
        setIsSpeaking(true);
      };

      recognition.onspeechend = () => {
        setIsSpeaking(false);
      };

      recognition.onsoundend = () => {
        setIsSpeaking(false);
      };

      recognition.onresult = (event) => {
        let interimText = "";
        let finalText = "";

        for (let i = 0; i < event.results.length; i++) {
          const res = event.results[i];
          if (res.isFinal) {
            finalText += res[0].transcript;
          } else {
            interimText += res[0].transcript;
          }
        }

        const fullText = (finalText + (interimText ? " " + interimText : "")).trim();
        if (fullText) {
          setVoiceTranscript(fullText);
          setIsSpeaking(true);
          setVoiceStatus("speaking");

          // Check if spoken words match any query from backend data / marketplace categories
          const isMarketPage =
            currentPath.startsWith("/buy-") ||
            currentPath.startsWith("/find-") ||
            currentPath.startsWith("/Buy") ||
            currentPath.startsWith("/recycle") ||
            currentPath.startsWith("/Farming") ||
            currentPath.startsWith("/single-item") ||
            currentPath.startsWith("/rawmaterial-") ||
            currentPath.startsWith("/manufacturer-") ||
            currentPath.startsWith("/wholeseller-") ||
            currentPath.startsWith("/retailer-");

          const backendMatch = matchBackendQuery(fullText);

          if (backendMatch) {
            // MATCH FOUND IN BACKEND DATA (even inside large/long sentences)!
            setMatchedQuery(backendMatch);
            setNoMatchFound(false);

            if (autoSearchTimerRef.current) {
              clearTimeout(autoSearchTimerRef.current);
            }

            // Automatically execute search and route when backend query matches
            autoSearchTimerRef.current = setTimeout(() => {
              executeVoiceSearch(fullText, backendMatch);
            }, 850);
          } else if (isMarketPage && fullText.length >= 2) {
            // User is already on an active market page querying that market's backend database
            const pageMatch = {
              name: "Marketplace Data",
              route: currentPath,
              query: fullText,
              matchedKeyword: fullText,
            };
            setMatchedQuery(pageMatch);
            setNoMatchFound(false);

            if (autoSearchTimerRef.current) {
              clearTimeout(autoSearchTimerRef.current);
            }

            autoSearchTimerRef.current = setTimeout(() => {
              executeVoiceSearch(fullText, pageMatch);
            }, 850);
          } else {
            // Does not match backend data yet — do not trigger search on unmatched words
            setMatchedQuery(null);
            if (autoSearchTimerRef.current) {
              clearTimeout(autoSearchTimerRef.current);
            }
            if (event.results[event.results.length - 1]?.isFinal) {
              setNoMatchFound(true);
            }
          }
        }
      };

      recognition.onerror = (err) => {
        console.warn("Speech recognition error:", err);
        setIsSpeaking(false);
        if (err.error === "not-allowed") {
          setErrorMessage("Microphone access denied. Please allow microphone in browser settings.");
          setVoiceStatus("error");
          setIsListening(false);
        } else if (err.error === "no-speech") {
          setVoiceStatus("idle");
        } else {
          setVoiceStatus("idle");
          setIsListening(false);
        }
      };

      recognition.onend = () => {
        setIsSpeaking(false);
        setIsListening(false);
        setVoiceStatus((prev) => (prev === "searching" ? "searching" : "idle"));
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (e) {
      console.error("Failed to start voice recognition:", e);
      setErrorMessage("Could not start microphone.");
      setVoiceStatus("error");
      setIsListening(false);
      setIsSpeaking(false);
    }
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {}
    }
    setIsListening(false);
    setIsSpeaking(false);
  };

  const openVoiceModal = (e) => {
    if (e && typeof e.stopPropagation === "function") {
      e.stopPropagation();
    }
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    setIsVoiceModalOpen(true);
    setVoiceTranscript("");
    setErrorMessage("");
    setMatchedQuery(null);
    setNoMatchFound(false);
    setVoiceStatus("listening");
    startVoiceSearch();
  };

  const closeVoiceModal = () => {
    if (autoSearchTimerRef.current) {
      clearTimeout(autoSearchTimerRef.current);
    }
    stopListening();
    setIsVoiceModalOpen(false);
    setVoiceTranscript("");
    setIsSpeaking(false);
    setVoiceStatus("idle");
    setErrorMessage("");
    setMatchedQuery(null);
    setNoMatchFound(false);
  };

  const handleHeroMicClick = () => {
    if (isListening) {
      stopListening();
      setVoiceStatus("idle");
    } else {
      setVoiceTranscript("");
      setMatchedQuery(null);
      setNoMatchFound(false);
      startVoiceSearch();
    }
  };

  const executeVoiceSearch = (queryToUse, matchInfo) => {
    const rawText = (queryToUse !== undefined ? queryToUse : voiceTranscript).trim();
    if (!rawText) return;

    const isMarketPage =
      currentPath.startsWith("/buy-") ||
      currentPath.startsWith("/find-") ||
      currentPath.startsWith("/Buy") ||
      currentPath.startsWith("/recycle") ||
      currentPath.startsWith("/Farming") ||
      currentPath.startsWith("/single-item") ||
      currentPath.startsWith("/rawmaterial-") ||
      currentPath.startsWith("/manufacturer-") ||
      currentPath.startsWith("/wholeseller-") ||
      currentPath.startsWith("/retailer-");

    const match = matchInfo || matchBackendQuery(rawText);

    // ONLY search if query matches backend data or user is on a marketplace page
    if (!isMarketPage && !match) {
      setNoMatchFound(true);
      return;
    }

    if (autoSearchTimerRef.current) {
      clearTimeout(autoSearchTimerRef.current);
    }

    setVoiceStatus("searching");
    stopListening();

    // Use the extracted query keyword if text is large/long sentence, so backend query matches items
    const queryForSearch = match && match.query ? match.query : rawText;

    setTimeout(() => {
      setSearchQuery(queryForSearch);
      if (match && match.route && match.route !== currentPath && !isMarketPage) {
        navigate(`${match.route}?search=${encodeURIComponent(queryForSearch)}`);
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("selectezi:voicesearch", {
              detail: { query: queryForSearch, rawQuery: rawText, pathname: match.route },
            })
          );
        }
      } else {
        triggerSearch(queryForSearch);
      }
      closeVoiceModal();
    }, 450);
  };

  const handleSuggestionClick = (suggestion) => {
    setVoiceTranscript(suggestion);
    executeVoiceSearch(suggestion);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);

    const isMarketPage =
      currentPath.startsWith("/buy-") ||
      currentPath.startsWith("/find-") ||
      currentPath.startsWith("/Buy") ||
      currentPath.startsWith("/recycle") ||
      currentPath.startsWith("/Farming") ||
      currentPath.startsWith("/single-item") ||
      currentPath.startsWith("/rawmaterial-") ||
      currentPath.startsWith("/manufacturer-") ||
      currentPath.startsWith("/wholeseller-") ||
      currentPath.startsWith("/retailer-");

    if (isMarketPage) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        const trimmed = val.trim();
        navigate(`${currentPath}${trimmed ? `?search=${encodeURIComponent(trimmed)}` : ""}`, {
          replace: true,
        });
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("selectezi:voicesearch", {
              detail: { query: trimmed, pathname: currentPath },
            })
          );
        }
      }, 350);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      triggerSearch();
    }
  };

  const handleClear = () => {
    setSearchQuery("");
    const isMarketPage =
      currentPath.startsWith("/buy-") ||
      currentPath.startsWith("/find-") ||
      currentPath.startsWith("/Buy") ||
      currentPath.startsWith("/recycle") ||
      currentPath.startsWith("/Farming") ||
      currentPath.startsWith("/single-item") ||
      currentPath.startsWith("/rawmaterial-") ||
      currentPath.startsWith("/manufacturer-") ||
      currentPath.startsWith("/wholeseller-") ||
      currentPath.startsWith("/retailer-");

    if (isMarketPage) {
      navigate(currentPath, { replace: true });
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("selectezi:voicesearch", {
            detail: { query: "", pathname: currentPath },
          })
        );
      }
    }

    if (isOverlayOpen && overlayInputRef.current) {
      overlayInputRef.current.focus();
    } else if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      if (autoSearchTimerRef.current) {
        clearTimeout(autoSearchTimerRef.current);
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {}
      }
    };
  }, []);

  // Hide search bar on main landing page and buying side dashboard
  const isBuyingSideDashboard =
    currentPath.toLowerCase() === "/buyingside" ||
    currentPath.toLowerCase().startsWith("/buyingside/") ||
    currentPath.toLowerCase() === "/buying-side" ||
    currentPath.toLowerCase().startsWith("/buying-side/");

  const isLandingPage =
    currentPath === "/" ||
    currentPath === "/pricing" ||
    currentPath === "/guideline" ||
    currentPath === "/showcase" ||
    currentPath === "/selectezi-benefits" ||
    currentPath === "/investment-opportunities" ||
    currentPath === "/hire-marketing-agent" ||
    currentPath === "/marketing-agent" ||
    currentPath.startsWith("/landing") ||
    currentPath.includes("marketing") ||
    currentPath.includes("investment");

  if (isLandingPage || isBuyingSideDashboard) {
    return null;
  }

  return (
    <>
      {/* 1. Inline Search Bar - Desktop & Tablet */}
      <form
        className={`navbar-search-form navbar-search-desktop ${isListening ? "is-listening" : ""} ${isFocused ? "is-focused" : ""} ${className}`.trim()}
        onSubmit={(e) => {
          e.preventDefault();
          triggerSearch();
        }}
        role="search"
      >
        <div className="navbar-search-wrapper">
          <button
            type="button"
            className="navbar-search-icon-btn"
            onClick={() => triggerSearch()}
            aria-label="Submit search"
            title="Search"
          >
            <SearchComp className="navbar-search-icon" />
          </button>

          <input
            ref={inputRef}
            type="text"
            className="navbar-search-input"
            placeholder={isListening ? "Listening... speak now" : placeholder}
            value={searchQuery}
            onChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={handleKeyDown}
            aria-label="Search"
          />

          {searchQuery && !isListening && (
            <button
              type="button"
              className="navbar-search-clear-btn"
              onClick={handleClear}
              aria-label="Clear search"
              title="Clear"
            >
              <CloseComp className="navbar-clear-icon" />
            </button>
          )}

          <button
            type="button"
            className={`navbar-search-voice-btn ${isListening ? "listening" : ""}`}
            onClick={openVoiceModal}
            aria-label="Search with voice"
            title="Click to search with voice"
          >
            <MicComp className="navbar-voice-icon" />
            {isListening && <span className="navbar-voice-pulse" />}
          </button>
        </div>
      </form>

      {/* 2. Mobile Search Icon Trigger Button (< 768px) */}
      <button
        type="button"
        className="navbar-search-mobile-trigger-btn"
        onClick={() => setIsOverlayOpen(true)}
        aria-label="Open search bar"
        title="Search"
      >
        <SearchComp className="navbar-search-mobile-icon" />
      </button>

      {/* 3. Search Overlay for Tablet & Mobile */}
      {isOverlayOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="navbar-search-overlay-backdrop"
            onClick={() => {
              if (isListening) stopListening();
              setIsOverlayOpen(false);
            }}
          >
            <div
              className="navbar-search-overlay-container"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Search"
            >
              {/* Back / Close Button */}
              <button
                type="button"
                className="navbar-search-overlay-back-btn"
                onClick={() => {
                  if (isListening) stopListening();
                  setIsOverlayOpen(false);
                }}
                aria-label="Close search overlay"
                title="Back"
              >
                <ArrowBackComp className="navbar-search-overlay-back-icon" />
              </button>

              {/* Overlay Input Form with Voice Button */}
              <form
                className="navbar-search-overlay-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  triggerSearch();
                  setIsOverlayOpen(false);
                }}
              >
                <div className={`navbar-search-overlay-wrapper ${isListening ? "is-listening" : ""}`}>
                  <button
                    type="submit"
                    className="navbar-search-icon-btn"
                    aria-label="Submit search"
                    title="Search"
                  >
                    <SearchComp className="navbar-search-icon" />
                  </button>

                  <input
                    ref={overlayInputRef}
                    type="text"
                    className="navbar-search-input navbar-search-overlay-input"
                    placeholder="Search marketplace..."
                    value={searchQuery}
                    onChange={handleInputChange}
                    aria-label="Search marketplace"
                  />

                  {searchQuery && (
                    <button
                      type="button"
                      className="navbar-search-clear-btn"
                      onClick={handleClear}
                      aria-label="Clear search"
                      title="Clear"
                    >
                      <CloseComp className="navbar-clear-icon" />
                    </button>
                  )}

                  <button
                    type="button"
                    className={`navbar-search-voice-btn ${isListening ? "listening" : ""}`}
                    onClick={openVoiceModal}
                    aria-label="Search with voice"
                    title="Search with voice"
                  >
                    <MicComp className="navbar-voice-icon" />
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* 4. YouTube-Style Animated & Fully Responsive Voice Search Modal */}
      {isVoiceModalOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="yt-voice-modal-backdrop"
            onClick={closeVoiceModal}
            role="dialog"
            aria-modal="true"
            aria-label="Search with your voice"
          >
            <div
              className="yt-voice-modal-card"
              onClick={(e) => e.stopPropagation()}
            >
              {/* YouTube Top Bar: Title & Close Button */}
              <div className="yt-voice-modal-header">
                <h2 className="yt-voice-modal-title">Search with your voice</h2>
                <button
                  type="button"
                  className="yt-voice-modal-close-btn"
                  onClick={closeVoiceModal}
                  aria-label="Close voice search"
                  title="Close (Esc)"
                >
                  <CloseComp className="yt-voice-close-icon" />
                </button>
              </div>

              {/* YouTube Live Typewriter Text Area */}
              <div className="yt-voice-transcript-container">
                {errorMessage ? (
                  <p className="yt-voice-error-text">{errorMessage}</p>
                ) : voiceTranscript ? (
                  <div className="yt-voice-speech-wrapper">
                    <p className="yt-voice-transcript-text">
                      {voiceTranscript}
                      <span className="yt-voice-cursor">|</span>
                    </p>

                    {/* Backend Data Query Match Indicator */}
                    {matchedQuery ? (
                      <div className="yt-voice-match-indicator">
                        <span className="yt-voice-match-check">✓</span>
                        <span>
                          Matched backend query: <strong>{matchedQuery.name}</strong>{" "}
                          ({matchedQuery.matchedKeyword})
                        </span>
                      </div>
                    ) : noMatchFound ? (
                      <div className="yt-voice-no-match-indicator">
                        <span>
                          No match in backend data. Try saying: food, cars, property, clothes, shoes, jobs...
                        </span>
                      </div>
                    ) : null}
                  </div>
                ) : voiceStatus === "searching" ? (
                  <p className="yt-voice-status-text">Searching backend data...</p>
                ) : isListening ? (
                  <p className="yt-voice-status-text">
                    Listening...
                    <span className="yt-voice-cursor idle">|</span>
                  </p>
                ) : (
                  <p className="yt-voice-status-text muted">Didn't hear that. Try again.</p>
                )}
              </div>

              {/* YouTube Hero Microphone with Bouncing Red Background Aura & Rings */}
              <div className="yt-voice-mic-container">
                {/* Concentric Bouncing Red Sound Wave Ripples */}
                <div
                  className={`yt-voice-wave-ring ring-1 ${isSpeaking ? "speaking" : isListening ? "listening" : ""}`}
                />
                <div
                  className={`yt-voice-wave-ring ring-2 ${isSpeaking ? "speaking" : isListening ? "listening" : ""}`}
                />
                <div
                  className={`yt-voice-wave-ring ring-3 ${isSpeaking ? "speaking" : isListening ? "listening" : ""}`}
                />

                {/* Bouncing Background Glow Aura */}
                <div
                  className={`yt-voice-bg-aura ${isSpeaking ? "speaking" : isListening ? "listening" : ""}`}
                />

                {/* YouTube Microphone Button (Red when listening, Grey when idle) */}
                <button
                  type="button"
                  className={`yt-voice-mic-btn ${isSpeaking ? "speaking" : isListening ? "listening" : "idle"}`}
                  onClick={handleHeroMicClick}
                  aria-label={isListening ? "Stop listening" : "Tap to speak"}
                  title={isListening ? "Listening... click to pause" : "Tap to speak"}
                >
                  <MicComp className="yt-voice-mic-icon" />
                </button>
              </div>

              {/* YouTube Subtitle Helper */}
              <div className="yt-voice-footer-hint">
                {voiceStatus === "searching" ? (
                  <span>Searching backend data...</span>
                ) : matchedQuery ? (
                  <span>✓ Match found in backend data! Searching now...</span>
                ) : noMatchFound ? (
                  <span>Query did not match backend data. Tap mic to try again</span>
                ) : isListening ? (
                  <span>Listening... say a category, product or service to search</span>
                ) : (
                  <span>Tap microphone to try again</span>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export { NavbarSearch };
export default NavbarSearch;
